import React from 'react';
import styles from './ClientsTable.module.scss';
import CustomTable from '../../../Common/CustomTable';
import AvatarIcon from '../../../Common/AvatarIcon';
import { ReactComponent as WhatsappIcon } from '../../../../assets/svg/icons/whatsapp.svg';
import { ReactComponent as PhoneIcon } from '../../../../assets/svg/icons/chiamate.svg';
import { ReactComponent as MailIcon } from '../../../../assets/svg/icons/mail.svg';
import { ReactComponent as NewIcon } from 'assets/svg/badgets/new_client.svg';
import { ReactComponent as BadIcon } from 'assets/svg/icons/warning_user.svg';
import { modalActions } from '../../../Modals/store';
import { useDispatch, useSelector } from 'react-redux';
import IconButton from '../../../Common/Buttons/IconButton';
import clx from 'classnames';
import { setCategory } from '../constants';
import dayjs from 'dayjs';
import { selectBadClients } from 'Containers/Clients/store/selectors';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  CalendarDays,
  UserCheck,
  UserMinus,
  UserPlus,
  UserX,
  Users,
  Sparkles,
} from 'lucide-react';

const STAT_ITEMS = [
  {
    key: 'registeredClients',
    labelKey: 'clients.table.stats.registered',
    filterId: 0,
    icon: Users,
    tone: 'total',
  },
  {
    key: 'activeClients',
    labelKey: 'clients.table.stats.active',
    filterId: 1,
    icon: UserCheck,
    tone: 'active',
  },
  {
    key: 'nonActiveClients',
    labelKey: 'clients.table.stats.nonActive',
    filterId: 2,
    icon: UserMinus,
    tone: 'inactive',
  },
  {
    key: 'disabledClients',
    labelKey: 'clients.table.stats.disabled',
    filterId: 3,
    icon: UserX,
    tone: 'disabled',
  },
  {
    key: 'fromSalonClients',
    labelKey: 'clients.table.stats.fromSalon',
    filterId: 4,
    icon: UserPlus,
    tone: 'fromSalon',
  },
  {
    key: 'newClients',
    labelKey: 'clients.table.stats.new',
    filterId: 5,
    icon: Sparkles,
    tone: 'new',
  },
];

export default ({
  values,
  clientsStats,
  isMobile,
  activeFilterId,
  setActiveFilterId,
  loading = false,
  initialLoading = false,
  refreshing = false,
  serverSidePagination = false,
  setPaginationValue,
  pageCount,
  pageRows = 20,
  entityId,
}) => {
  const { t, i18n } = useTranslation();
  const badClients = useSelector(selectBadClients);
  const badClientIds = React.useMemo(() => {
    const ids = new Set();
    (badClients || []).forEach((item) => {
      if (item?.utente != null) ids.add(item.utente);
    });
    return ids;
  }, [badClients]);
  const dispatch = useDispatch();
  const data = React.useMemo(() => values, [values]);
  const showInitialSkeleton = (initialLoading || loading) && (!values || values.length === 0);
  const showRefreshing = refreshing && !showInitialSkeleton;

  const openClient = (original) => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'clientModal',
        props: { client_id: original.value, totPren: original.totAppointments },
      }),
    );
  };

  const daysAppointment = (date) => {
    if (date) {
      let days = dayjs().diff(date, 'day');
      if (days > 0) {
        return t('clients.table.lastBookingDays', { days });
      } else if (days == 0) {
        return t('clients.table.lastBookingToday');
      } else {
        return t('clients.table.nextBookingInDays', { days: `${`${days}`.replace('-', '')}` });
      }
    }
    return t('clients.table.noBookingsYet');
  };

  const bookingsPill = (count) => {
    if (count == null) {
      return (
        <span className={styles.bookings_pill} aria-busy="true">
          <CalendarDays size={12} strokeWidth={2.4} aria-hidden="true" />
          …
        </span>
      );
    }
    return (
      <span
        className={styles.bookings_pill}
        title={t('clients.table.bookingsCount', { count })}
      >
        <CalendarDays size={12} strokeWidth={2.4} aria-hidden="true" />
        {t('clients.table.bookingsCountShort', { count })}
      </span>
    );
  };

  const clientSubtitle = (original) => {
    const category =
      original.category >= 0 ? setCategory(original.category) : null;
    const bookingLabel =
      original.totAppointments == null && original.lastAppointment == null
        ? '…'
        : daysAppointment(original.lastAppointment);
    return [category, bookingLabel].filter(Boolean).join(' · ');
  };

  const columns = React.useMemo(
    () =>
      isMobile
        ? [
            {
              Header: '',
              accessor: 'actions',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.client_mobile_card}>
                    <div className={styles.avatar_container}>
                      <AvatarIcon name={original.name} icon={original.icon} size={'44px'} />
                      {Number.isFinite(original.totAppointments) &&
                        original.totAppointments < 2 && <NewIcon style={{ bottom: '0px' }} />}
                      {badClientIds.has(original.idutente) && (
                        <BadIcon style={{ top: '0px', width: '18px', height: '18px' }} />
                      )}
                    </div>
                    <div className={styles.card_info_container}>
                      <div className={styles.card_avatar_social}>
                        <button
                          type="button"
                          className={styles.card_avatar_name}
                          onClick={() => openClient(original)}
                        >
                          <span>{original.name}</span>
                          {bookingsPill(original.totAppointments)}
                        </button>
                        <div className={styles.card_social}>
                          <IconButton
                            svg={<WhatsappIcon />}
                            type={'whatsapp'}
                            size={'40px'}
                            isActive={!!original.phone}
                            value={original.phone}
                            margin={'0 8px 0 0'}
                          />
                          <IconButton
                            svg={<MailIcon />}
                            type={'email'}
                            size={'40px'}
                            isActive={!!original.email}
                            value={original.email}
                            margin={'0 8px 0 0'}
                          />
                          <IconButton
                            svg={<PhoneIcon />}
                            type={'phone'}
                            size={'40px'}
                            isActive={!!original.phone}
                            value={original.phone}
                            margin={'0 8px 0 0'}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        className={styles.link}
                        onClick={() => openClient(original)}
                      >
                        <ChevronRight size={18} strokeWidth={2.2} />
                      </button>
                    </div>
                  </div>
                );
              },
            },
          ]
        : [
            {
              Header: '',
              accessor: 'name',
              Cell: ({ row: { original } }) => {
                return (
                  <button
                    type="button"
                    className={styles.name_cell_container}
                    onClick={() => openClient(original)}
                  >
                    <div className={styles.avatar_container}>
                      <AvatarIcon name={original.name} icon={original.icon} size={'48px'} />
                      {Number.isFinite(original.totAppointments) &&
                        original.totAppointments < 2 && <NewIcon style={{ bottom: '0px' }} />}
                      {badClientIds.has(original.idutente) && (
                        <BadIcon style={{ top: '0px', width: '20px', height: '20px' }} />
                      )}
                    </div>
                    <div className={styles.client_info}>
                      <div className={styles.name_row}>
                        <div className={styles.name}>{original.name}</div>
                        {bookingsPill(original.totAppointments)}
                      </div>
                      <div className={styles.description}>{clientSubtitle(original)}</div>
                    </div>
                  </button>
                );
              },
            },
            {
              Header: '',
              accessor: 'actions',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.client_actions}>
                    <IconButton
                      svg={<WhatsappIcon />}
                      type={'whatsapp'}
                      size={'40px'}
                      isActive={!!original.phone}
                      value={original.phone}
                      margin={'0 8px 0 0'}
                    />
                    <IconButton
                      svg={<PhoneIcon />}
                      type={'phone'}
                      size={'40px'}
                      isActive={!!original.phone}
                      value={original.phone}
                      margin={'0 8px 0 0'}
                    />
                    <IconButton
                      svg={<MailIcon />}
                      type={'email'}
                      size={'40px'}
                      isActive={!!original.email}
                      value={original.email}
                      margin={'0 8px 0 0'}
                    />
                  </div>
                );
              },
            },
            {
              Header: '',
              accessor: 'link',
              Cell: ({ row: { original } }) => {
                return (
                  <button
                    type="button"
                    className={styles.link}
                    onClick={() => openClient(original)}
                  >
                    {t('clients.table.details')}
                    <ChevronRight size={16} strokeWidth={2.2} />
                  </button>
                );
              },
            },
          ],
    [isMobile, badClientIds, t],
  );

  return (
    <div
      className={clx(styles.table_card, {
        [styles.table_card_mobile]: isMobile,
      })}
    >
      <div className={styles.header_container}>
        {STAT_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = activeFilterId === item.filterId;
          return (
            <button
              key={item.key}
              type="button"
              className={clx(styles.header_cell, styles[`header_cell_tone_${item.tone}`], {
                [styles.header_cell_selected]: isActive,
              })}
              aria-pressed={isActive}
              onClick={() => setActiveFilterId?.(item.filterId)}
            >
              <span className={styles.header_cell_icon}>
                <Icon size={16} strokeWidth={2.2} />
              </span>
              <span className={styles.header_cell_copy}>
                <span className={styles.name}>{t(item.labelKey)}</span>
                <span className={styles.value}>
                  {showInitialSkeleton || clientsStats == null
                    ? '…'
                    : (clientsStats?.[item.key] ?? 0).toLocaleString(i18n.language)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      {showInitialSkeleton ? (
        <div className={styles.table_skeleton} aria-busy="true" aria-label="loading">
          {[0, 1, 2, 3, 4, 5].map((row) => (
            <div key={row} className={styles.table_skeleton_row}>
              <span className={styles.table_skeleton_avatar} />
              <div className={styles.table_skeleton_text}>
                <span className={styles.table_skeleton_name} />
                <span className={styles.table_skeleton_meta} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          className={clx(styles.table_body, {
            [styles.table_body_refreshing]: showRefreshing,
          })}
          aria-busy={showRefreshing || undefined}
        >
          <CustomTable
            data={data}
            columns={columns}
            isMobile={isMobile}
            emptyTableMessage={t('clients.table.empty')}
            serverSidePagination={serverSidePagination}
            setPaginationValue={setPaginationValue}
            pageCount={pageCount}
            pageRows={pageRows}
            entityId={entityId}
            paginationPosition="both"
          />
        </div>
      )}
    </div>
  );
};
